import { Component, Input, OnDestroy } from '@angular/core';
import { MessageService } from 'primeng/api';
import { interval, startWith, Subscription, switchMap } from 'rxjs';
import { DispatchService, DispatchStatus } from '../../services/dispatch.service';

/**
 * Bouton + tunnel de dispatch des ressources numériques vers une cible externe (MAX par défaut).
 *
 * Flux :
 *  1. clic sur le bouton -> dialog listant les structures dispatchables (cases à cocher),
 *  2. envoi -> lancement asynchrone côté back (renvoie un jobId),
 *  3. modal de progression en bas à droite, alimenté par polling du statut,
 *  4. recap final (succès / échecs / erreurs).
 */
@Component({
  selector: 'app-dispatch-max',
  templateUrl: './dispatch-max.component.html',
  styleUrls: ['./dispatch-max.component.scss'],
})
export class DispatchMaxComponent implements OnDestroy {
  @Input() ouvrageId!: number;
  /** Type de dispatch (extensible : "max", puis d'autres à l'avenir) */
  @Input() type: string = 'max';

  /** Intervalle de polling du statut (ms) */
  private static readonly POLL_INTERVAL = 1500;

  displayStructures = false;
  loadingStructures = false;
  structures: string[] = [];
  selectedStructures: string[] = [];

  dispatching = false;
  showProgress = false;
  jobTotal = 0;
  job: DispatchStatus | null = null;

  private pollSub?: Subscription;

  constructor(
    private dispatchService: DispatchService,
    private messageService: MessageService,
  ) {}

  /** Ouvre le dialog et charge les structures dispatchables */
  openDialog(): void {
    this.structures = [];
    this.selectedStructures = [];
    this.loadingStructures = true;
    this.displayStructures = true;

    this.dispatchService.getStructures(this.type, this.ouvrageId).subscribe({
      next: (res) => {
        this.structures = res.structures ?? [];
        this.loadingStructures = false;
      },
      error: () => {
        this.loadingStructures = false;
        this.toastError('Impossible de charger les structures.');
      },
    });
  }

  selectAll(): void {
    this.selectedStructures = [...this.structures];
  }

  deselectAll(): void {
    this.selectedStructures = [];
  }

  /** Lance le dispatch des structures sélectionnées */
  send(): void {
    if (this.selectedStructures.length === 0 || this.dispatching) {
      return;
    }
    this.dispatching = true;

    this.dispatchService.dispatch(this.type, this.ouvrageId, this.selectedStructures).subscribe({
      next: (res) => {
        this.dispatching = false;
        this.displayStructures = false;
        this.jobTotal = res.total;
        this.job = null;
        this.showProgress = true;
        this.startPolling(res.jobId);
      },
      error: (err) => {
        this.dispatching = false;
        this.toastError(err?.error?.error ?? 'Échec du lancement du dispatch.');
      },
    });
  }

  /** Pourcentage de progression (0-100) */
  get progressPercent(): number {
    const total = this.job?.total ?? this.jobTotal;
    if (!total) {
      return 0;
    }
    return Math.round(((this.job?.processed ?? 0) / total) * 100);
  }

  closeProgress(): void {
    this.showProgress = false;
    this.job = null;
    this.stopPolling();
  }

  ngOnDestroy(): void {
    this.stopPolling();
  }

  private startPolling(jobId: string): void {
    this.stopPolling();
    this.pollSub = interval(DispatchMaxComponent.POLL_INTERVAL)
      .pipe(
        startWith(0),
        switchMap(() => this.dispatchService.getStatus(jobId)),
      )
      .subscribe({
        next: (status) => {
          this.job = status;
          if (status.done) {
            this.stopPolling();
          }
        },
        error: () => {
          this.stopPolling();
          this.toastError('Suivi du dispatch interrompu.');
        },
      });
  }

  private stopPolling(): void {
    this.pollSub?.unsubscribe();
    this.pollSub = undefined;
  }

  private toastError(detail: string): void {
    this.messageService.add({ key: 'dispatch', severity: 'error', summary: 'Dispatch', detail });
  }
}
